import { apiClient } from '../utils/api-client';

export interface PushSubscriptionStatus {
  has_subscription: boolean;
  subscription_count: number;
}

/**
 * Which ask a subscription came from, stored on the server so the funnel does
 * not depend on who accepted analytics cookies (docs/PUSH_OPT_IN_PLAN.md).
 */
export type PushSource =
  | 'dashboard_card'
  | 'climbs_card'
  | 'notifications_card'
  | 'follow_gym'
  | 'follow_crag'
  | 'follow_user'
  | 'send'
  | 'profile_toggle'
  | 'settings_toggle'
  | 'gym_bell';

export interface VapidPublicKey {
  public_key: string;
}

// Helper to convert base64 VAPID key to Uint8Array
function urlBase64ToUint8Array(base64String: string): Uint8Array {
  const padding = '='.repeat((4 - base64String.length % 4) % 4);
  const base64 = (base64String + padding)
    .replace(/-/g, '+')
    .replace(/_/g, '/');

  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);

  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

// Check if push notifications are supported
export function isPushNotificationSupported(): boolean {
  return 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;
}

// Check if we're on iOS/iPadOS
export function isIOSOrIPadOS(): boolean {
  const userAgent = window.navigator.userAgent.toLowerCase();
  const hasIOSUserAgent = /iphone|ipad|ipod/.test(userAgent);
  // iPad detection: reports MacIntel but has touch capability
  // Note: Some Macs with trackpad may have maxTouchPoints > 0, but iPads have maxTouchPoints > 1
  // Also check for absence of "mac os x" in userAgent for iPad (iPad Safari doesn't include it in some modes)
  const isIPadMaskedAsMac = navigator.platform === 'MacIntel' &&
                             navigator.maxTouchPoints > 1 &&
                             !userAgent.includes('chrome'); // Chrome on Mac is NOT iPad

  return hasIOSUserAgent || isIPadMaskedAsMac;
}

// Check if we're on macOS
export function isMacOS(): boolean {
  return navigator.platform.toLowerCase().includes('mac') && !isIOSOrIPadOS();
}

// Check if the app is installed (running in standalone mode)
export function isAppInstalled(): boolean {
  // Check if running in standalone mode (PWA installed)
  const isStandalone = window.matchMedia('(display-mode: standalone)').matches;
  const isIOSStandalone = (window.navigator as { standalone?: boolean }).standalone === true;

  return isStandalone || isIOSStandalone;
}

// Check if push notifications are enabled for iOS
export function canRequestIOSNotifications(): boolean {
  if (!isIOSOrIPadOS()) {
    return true; // Not iOS, so no special restrictions
  }

  // On iOS, push notifications can only be requested if the app is installed
  return isAppInstalled();
}

// Get current notification permission
export function getNotificationPermission(): NotificationPermission {
  if (!isPushNotificationSupported()) {
    return 'denied';
  }
  return Notification.permission;
}

// Request notification permission
export async function requestNotificationPermission(): Promise<NotificationPermission> {
  if (!isPushNotificationSupported()) {
    throw new Error('Push notifications are not supported in this browser');
  }

  if (isIOSOrIPadOS() && !isAppInstalled()) {
    throw new Error('On iOS, notifications can only be enabled after installing the app');
  }

  const permission = await Notification.requestPermission();
  return permission;
}

// Get VAPID public key from server
export async function getVapidPublicKey(): Promise<string> {
  const response = await apiClient.get('/api/push_subscriptions/vapid_public_key', { requireAuth: true });
  if (!response.ok) {
    throw new Error('Failed to get VAPID public key');
  }
  const data = await response.json() as VapidPublicKey;
  return data.public_key;
}

// Subscribe to push notifications. On a browser another account already
// subscribed, the browser hands back the same endpoint and this account gets
// its own registration for it.
export async function subscribeToPushNotifications(source?: PushSource): Promise<PushSubscription> {
  if (!isPushNotificationSupported()) {
    throw new Error('Push notifications are not supported');
  }

  // Get service worker registration
  const registration = await navigator.serviceWorker.ready;

  // Get VAPID public key
  const vapidPublicKey = await getVapidPublicKey();
  const convertedVapidKey = urlBase64ToUint8Array(vapidPublicKey);

  // Subscribe to push notifications
  const subscription = await registration.pushManager.subscribe({
    userVisibleOnly: true,
    applicationServerKey: convertedVapidKey
  });

  // Send subscription to server
  await savePushSubscription(subscription, source);

  return subscription;
}

// Save push subscription to server
async function savePushSubscription(subscription: PushSubscription, source?: PushSource): Promise<void> {
  const subscriptionJson = subscription.toJSON();

  const response = await apiClient.post('/api/push_subscriptions', {
    subscription: {
      endpoint: subscription.endpoint,
      p256dh_key: subscriptionJson.keys?.p256dh,
      auth_key: subscriptionJson.keys?.auth,
      source
    }
  }, { requireAuth: true });

  if (!response.ok) {
    throw new Error('Failed to save push subscription');
  }
}

// Turn push off for the signed-in account on this device. Other accounts on
// the same device keep theirs: the browser itself unsubscribes only once the
// server says nobody is left on it.
export async function unsubscribeFromPushNotifications(): Promise<void> {
  if (!isPushNotificationSupported()) {
    return;
  }

  const registration = await navigator.serviceWorker.ready;
  const subscription = await registration.pushManager.getSubscription();

  if (subscription) {
    const encodedEndpoint = encodeURIComponent(subscription.endpoint);
    const response = await apiClient.delete(`/api/push_subscriptions?endpoint=${encodedEndpoint}`, {
      requireAuth: true
    });
    const { remaining_on_device: remaining } = await response.json().catch(() => ({})) as { remaining_on_device?: number };

    if (remaining === 0) {
      await subscription.unsubscribe();
    }
  }
}

// Whether this browser's subscription is registered for the signed-in account.
// Keeps that registration counting as a live device; never creates one.
export async function syncPushSubscription(endpoint: string): Promise<boolean> {
  const response = await apiClient.post('/api/push_subscriptions/sync', { endpoint }, { requireAuth: true });
  if (!response.ok) {
    throw new Error('Failed to sync push subscription');
  }
  const data = await response.json() as { registered: boolean };
  return data.registered;
}

// Check if user has an active push subscription
export async function getPushSubscriptionStatus(): Promise<PushSubscriptionStatus> {
  const response = await apiClient.get('/api/push_subscriptions/status', { requireAuth: true });
  if (!response.ok) {
    throw new Error('Failed to get subscription status');
  }
  const data = await response.json() as PushSubscriptionStatus;
  return data;
}

// Get current push subscription
export async function getCurrentPushSubscription(): Promise<PushSubscription | null> {
  if (!isPushNotificationSupported()) {
    return null;
  }

  const registration = await navigator.serviceWorker.ready;
  return registration.pushManager.getSubscription();
}

export const pushNotificationService = {
  isPushNotificationSupported,
  isIOSOrIPadOS,
  isMacOS,
  isAppInstalled,
  canRequestIOSNotifications,
  getNotificationPermission,
  requestNotificationPermission,
  subscribeToPushNotifications,
  unsubscribeFromPushNotifications,
  getPushSubscriptionStatus,
  getCurrentPushSubscription,
  syncPushSubscription
};
